<template>
  <v-menu
    ref="menu"
    v-model="open"
    offset-y
    left
    nudge-bottom="6"
    min-width="0"
    :close-on-content-click="false"
    content-class="app-menu"
    role="none"
  >
    <template v-slot:activator="{ on, attrs }">
      <button
        ref="button"
        type="button"
        class="app-ib theme-toggle"
        :class="{ 'app-ib--pressed': dunkel }"
        :style="sizeStyle"
        aria-label="Darstellung"
        :title="tooltipOn ? 'Darstellung' : null"
        v-bind="attrs"
        v-on="on"
        @click="onActivatorClick"
      >
        <app-icon name="moon" :size="iconSize" :style="iconStyle" />
      </button>
    </template>
    <div
      class="app-menu__list theme-menu"
      :class="{ 'theme-menu--touch': touch }"
      role="menu"
      aria-label="Darstellung"
      @keydown.tab="onTab"
    >
      <div role="group" aria-label="Farbschema">
        <div class="app-menu__header" role="presentation" aria-hidden="true">Farbschema</div>
        <button
          v-for="schema in schemata"
          :key="schema.value"
          type="button"
          class="v-list-item app-menu__item theme-menu__item"
          role="menuitemradio"
          :aria-checked="$shell.theme === schema.value ? 'true' : 'false'"
          @click="setTheme(schema.value)"
          @keydown.enter.stop.prevent="setTheme(schema.value)"
        >
          <app-icon :name="schema.icon" :size="16" class="theme-menu__icon" />
          <span class="app-menu__text">{{ schema.label }}</span>
          <app-icon
            name="check"
            :size="14"
            class="app-menu__check"
            :class="{ 'theme-menu__check--leer': $shell.theme !== schema.value }"
          />
        </button>
      </div>
      <!-- Hauptfarbe nur für bconn_admin (Kundenwunsch 13.10.2026), sonst gilt Petrol -->
      <div v-if="$shell.hauptfarbeFrei" class="app-menu__divider" role="separator"></div>
      <div v-if="$shell.hauptfarbeFrei" role="group" aria-label="Hauptfarbe">
        <div class="app-menu__header" role="presentation" aria-hidden="true">Hauptfarbe</div>
        <button
          v-for="farbe in farben"
          :key="farbe.value"
          type="button"
          class="v-list-item app-menu__item theme-menu__item"
          role="menuitemradio"
          :aria-checked="$shell.hauptfarbe === farbe.value ? 'true' : 'false'"
          @click="setHauptfarbe(farbe.value)"
          @keydown.enter.stop.prevent="setHauptfarbe(farbe.value)"
        >
          <span class="theme-menu__swatch" :style="{ background: farbe.swatch }" aria-hidden="true"></span>
          <span class="app-menu__text">{{ farbe.label }}</span>
          <app-icon
            name="check"
            :size="14"
            class="app-menu__check"
            :class="{ 'theme-menu__check--leer': $shell.hauptfarbe !== farbe.value }"
          />
        </button>
      </div>
    </div>
  </v-menu>
</template>

<script>
// Menü „Darstellung“ am Mond-Knopf (Kundenwunsch 12.10.2026 „dark light mode button oben rechts links neben dem wifi
// symbol“, seit 13.10.2026 mit Hauptfarbe: „die primary color welche z.b. bei den charts im dashboard verwendet wird etc
// über ein optionsmenü z.b. bei dark light button anpassbar machen. das alte petrol unser neues blau oder unser primary
// orange“; docs/redesign/umsetzung.md, Nachtrag 12.10.2026 Nr. 15 und 19). Steht in der Systemleiste zwischen Suche und
// Verbindungsknopf, auf dem Handy bei offenem Mehr-Menü rechts in der App-Leiste, in der Hub-Kopfleiste vor dem Status.
// Knopf: rund wie der Verbindungsknopf (32, Touch 44 bzw. feste Größe size), Mond 16/20 in --ink-3; bei dunklem
// Farbschema gedrückt dargestellt (.app-ib--pressed: --sel, Kante --link, Mond --link). Name „Darstellung“,
// aria-haspopup/aria-expanded von v-menu.
// Menü (app-menu, rechtsbündig unter dem Knopf, Breite 208, Touch 240): zwei Gruppen mit Auswahl-Einträgen
// (menuitemradio, Häkchen am gewählten): „Farbschema“ Hell (Sonne) | Dunkel (Mond) und – nur für bconn_admin, sonst
// gilt Petrol – „Hauptfarbe“ Petrol (seit 13.10.2026 Standard) |
// Petrol (das frühere Navy) | Orange, je mit Farbtupfer in der hellen Grundfarbe. Eine Wahl wirkt sofort; das Menü
// bleibt offen, damit man vergleichen kann. Tastatur wie die übrigen Menüknöpfe (AppSelect): Enter, Leertaste oder Pfeil
// ab öffnen mit Fokus auf dem gewählten Farbschema, Pfeile wandern mit dem Fokus, Enter wählt, Esc schließt und gibt
// den Fokus an den Knopf, Tab schließt und geht vom Knopf aus weiter.
// Der Knopf ändert nur den Zustand ($shell.setTheme, $shell.setHauptfarbe); Klassen, Vuetify-Theme und Browserfarbe
// setzt MainLayout über src/shell/farbschema.js. Prop size: 'auto' (32, Touch 44) oder feste Größe in px (App-Leiste
// 44). Keine Ereignisse oder Slots.
import AppIcon from '../ui/AppIcon.vue';
import { raiseMenu, followMenuFocus, focusMenuItem } from '../ui/AppSelect.vue';
import { notePopup } from '../../shell/shellState';

const SCHEMATA = [
  { value: 'hell', label: 'Hell', icon: 'sun' },
  { value: 'dunkel', label: 'Dunkel', icon: 'moon' }
];
// Farbtupfer: die helle Grundfarbe der Hauptfarbe (--d-foc in tokens.css)
// Petrol zuerst: Standard ohne gespeicherte Wahl (Kundenwunsch 13.10.2026)
const FARBEN = [
  { value: 'petrol', label: 'Petrol', swatch: '#3d5a80' },
  { value: 'blau', label: 'Blau', swatch: '#2b7bb9' },
  { value: 'orange', label: 'Orange', swatch: '#ff6000' }
];

export default {
  name: 'AppThemeToggle',
  components: { AppIcon },
  props: {
    size: { type: [String, Number], default: 'auto' }
  },
  data() {
    return {
      open: false,
      mounted: false,
      schemata: SCHEMATA,
      farben: FARBEN
    };
  },
  computed: {
    dunkel() {
      return !!this.$shell && this.$shell.theme === 'dunkel';
    },
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    fixedSize() {
      const n = Number(this.size);
      return this.size !== 'auto' && n > 0 ? n : null;
    },
    sizeStyle() {
      return this.fixedSize ? { width: this.fixedSize + 'px', height: this.fixedSize + 'px' } : null;
    },
    // wie AppIconButton: ab 44 Icon 20, ab 36 Icon 18, sonst 16 (auto: 16, Touch 20 über --i-ib)
    iconSize() {
      const s = this.fixedSize;
      if (!s) {
        return 16;
      }
      return s >= 44 ? 20 : (s >= 36 ? 18 : 16);
    },
    iconStyle() {
      return this.fixedSize ? { width: this.iconSize + 'px', height: this.iconSize + 'px' } : null;
    },
    tooltipOn() {
      return this.mounted && !this.touch;
    }
  },
  watch: {
    open(isOpen) {
      notePopup(isOpen);
      // mit Enter, Leertaste oder Pfeil geöffnet: Fokus auf das gewählte Farbschema
      if (isOpen && this.keyboardOpen) {
        focusMenuItem(this.$refs.menu, '[aria-checked="true"]');
      }
      this.keyboardOpen = false;
    }
  },
  created() {
    // nicht reaktiv: zuletzt mit der Tastatur geöffnet (Klick mit detail 0)
    this.keyboardOpen = false;
  },
  mounted() {
    this.mounted = true;
    raiseMenu(this.$refs.menu);
    followMenuFocus(this, this.$refs.menu, () => this.open);
  },
  beforeDestroy() {
    if (this.open) {
      notePopup(false);
    }
  },
  methods: {
    onActivatorClick(e) {
      this.keyboardOpen = !!e && e.detail === 0;
    },
    // Knopf über die Referenz: Wurzel der Komponente ist v-menu, der Knopf liegt nicht zwingend darin
    focusButton() {
      const button = this.$refs.button;
      if (button && button.focus) {
        button.focus();
      }
    },
    // Tab im Menü: schließen und vom Knopf aus weiter
    onTab() {
      this.open = false;
      this.focusButton();
    },
    setTheme(value) {
      this.$shell.setTheme(value);
    },
    setHauptfarbe(value) {
      this.$shell.setHauptfarbe(value);
    }
  }
};
</script>

<style scoped>
/* rund wie der Verbindungsknopf; Icon --ink-3 wie Verbindungsknopf und Suche, bei Hover --ink-2 */
.theme-toggle {
  border-radius: 50%;
  color: var(--ink-3);
}
:where(html:not(.dichte-touch)) .theme-toggle:not(.app-ib--pressed):hover {
  color: var(--ink-2);
}
/* dunkel: Mond in --link auf --sel mit Kante --link (Fläche und Kante aus .app-ib--pressed) */
.theme-toggle.app-ib--pressed {
  color: var(--link);
}
@media print {
  .theme-toggle {
    display: none;
  }
}

/* Menü (im Portal; v-menu gibt der Fläche das Scope-Attribut dieser Komponente mit) */
.theme-menu {
  width: 208px;
}
.theme-menu--touch {
  width: 240px;
}
.theme-menu__item {
  gap: 10px;
}
/* Text nimmt den freien Platz: linksbündig neben Icon bzw. Farbtupfer, Häkchen rechts */
.theme-menu__item .app-menu__text {
  flex: 1 1 auto;
}
.theme-menu__icon {
  flex: none;
  color: var(--ink-3);
}
/* Farbtupfer 14 mit feiner Kante, damit er auch auf dunkler Fläche klar umrissen ist */
.theme-menu__swatch {
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(21, 26, 33, .16);
}
.theme-menu__check--leer {
  visibility: hidden;
}
</style>
